// 섬광 리미터(광과민 안전 백스톱, FLASH_SAFETY): 1초 3회 이하, 간격 334ms 이상, 1회 100ms 이하.
// 전체 화면 섬광은 각성 Flash 단계 1회 + AWAKEN.fullFlashSlashes 참격 1회만 요청된다. 그 밖의 번쩍임은 화면 일부(참격선)다.
import { FLASH_SAFETY } from '../shared/config';

/**
 * 우선 섬광(각성·처형) 대기 상한(ms, c015): 예산에 막힌 우선 섬광은 리미터가 허락하는 첫 순간에 건다. 이 시간 안에 허락되지 않으면 버린다
 * (늦은 섬광이 다른 장면에 떨어지지 않게). 공용 안전 예산(1초 3회·간격 334ms)은 그대로다 — 대기 섬광도 admit을 거친다
 */
export const PRIORITY_WAIT_MS = 400;

/** 대기 우선순위: 각성 섬광(표지 0) > 처형 섬광(표지 1) */
function rank(tag: number): number { return tag === 0 ? 2 : 1; }

export class FlashLimiter {
  private readonly times = new Float64Array(8);
  private n = 0;
  private last = -1e9;
  private until = -1;
  private start = 0;
  private dur = 1;
  private peak = 0;
  /** 막힌 우선 섬광(요청 시각, −1 = 없음)과 그 섬광 값 */
  private pendAt = -1;
  private pendDur = 0;
  private pendStrength = 0;
  private pendTag = 0;
  /** 이 시각(ms)까지 우선 섬광이 곧 온다: 옅은 화면 펄스(admitSoft)는 양보한다 */
  private reservedUntil = -1;
  /** 대기했다가 건 우선 섬광 수·대기 끝에 버린 수(QA) */
  deferred = 0;
  dropped = 0;
  /** 우선 섬광에 양보한 옅은 펄스 수(QA) */
  yielded = 0;
  /** 지금(마지막으로 허용된) 섬광의 색 표지: 0 흰색(각성), 1 흰금색(처형). 후처리가 색을 고른다 */
  tag = 0;
  /** 승인된 섬광 수(QA) */
  granted = 0;
  /** 리미터가 막은 요청 수(QA) */
  blocked = 0;

  /**
   * 섬광을 요청한다. 허용되면 true.
   * priority(각성·처형): 예산에 막히면 버리지 않고 한 개를 대기시켜 PRIORITY_WAIT_MS 안에 리미터가 허락하는 첫 순간(pump)에 건다(c015 —
   * 스킬 화면 펄스 직후 334ms 안의 각성·처형 섬광이 통째로 사라졌다). 대기 중이면 옅은 펄스가 양보한다. 반환은 지금 걸었는지
   */
  request(nowMs: number, durMs: number, strength: number, tag = 0, priority = false): boolean {
    if (priority) this.pump(nowMs);
    if (!this.admit(nowMs)) {
      if (priority) {
        // 대기 칸은 하나: 각성(표지 0)이 처형(표지 1)보다 먼저다. 낮은 쪽이 밀려나면 버린 수로 센다(PR #157 리뷰 P2)
        if (this.pendAt >= 0 && rank(tag) < rank(this.pendTag)) { this.dropped++; return false; }
        if (this.pendAt >= 0) this.dropped++;
        this.pendAt = nowMs; this.pendDur = durMs; this.pendStrength = strength; this.pendTag = tag;
      }
      return false;
    }
    if (priority) this.pendAt = -1;
    this.fire(nowMs, durMs, strength, tag);
    return true;
  }

  private fire(nowMs: number, durMs: number, strength: number, tag: number): void {
    this.start = nowMs;
    this.dur = Math.min(durMs, FLASH_SAFETY.maxFlashMs);
    this.until = nowMs + this.dur;
    this.peak = strength;
    this.tag = tag;
  }

  /** 대기 중인 우선 섬광을 리미터가 허락하면 건다. 대기 상한을 넘었으면 버린다. 매 프레임 value()가 부른다 */
  pump(nowMs: number): void {
    if (this.pendAt < 0) return;
    if (nowMs - this.pendAt > PRIORITY_WAIT_MS) { this.pendAt = -1; this.dropped++; return; }
    if (!this.wouldAdmit(nowMs)) return;
    this.admit(nowMs);
    this.pendAt = -1;
    this.deferred++;
    this.fire(nowMs, this.pendDur, this.pendStrength, this.pendTag);
  }

  /** 마지막으로 건 섬광의 시작 시각(ms, QA·시험: 대기 섬광이 실제로 걸린 순간을 잰다) */
  get startedAt(): number { return this.until < 0 ? -1 : this.start; }
  /** 예약을 푼다(각성의 마지막 전체 섬광을 건 뒤) */
  clearReserve(): void { this.reservedUntil = -1; }
  /** 우선 섬광이 곧 온다(각성 모으기 → 섬광 단계, 섬광 단계 → 6번째 참격): untilMs까지 옅은 펄스가 예산을 쓰지 않는다 */
  reserve(untilMs: number): void { this.reservedUntil = Math.max(this.reservedUntil, untilMs); }
  /** 대기·예약 중인 우선 섬광이 있는지 */
  priorityPending(nowMs: number): boolean { return this.pendAt >= 0 || nowMs < this.reservedUntil; }

  /**
   * 옅은 화면 펄스(스킬 VFX 모듈 화면 효과)의 예산 요청: 우선 섬광이 대기 중이거나 곧 올 예정이면 양보(거절)하고, 아니면 admit과 같다.
   * 양보해도 안전 예산(1초 3회·간격 334ms)은 admit이 그대로 지킨다
   */
  admitSoft(nowMs: number): boolean {
    this.pump(nowMs);
    if (this.priorityPending(nowMs)) { this.yielded++; this.blocked++; return false; }
    return this.admit(nowMs);
  }

  /** admit과 같은 판정이지만 기록하지 않는다 */
  private wouldAdmit(nowMs: number): boolean {
    if (nowMs - this.last < FLASH_SAFETY.minGapMs) return false;
    let recent = 0;
    for (let i = 0; i < this.times.length; i++) if (this.times[i] > 0 && nowMs - this.times[i] < 1000) recent++;
    return recent < FLASH_SAFETY.maxPerSecond;
  }

  /**
   * 공용 섬광 예산만 쓴다(이 리미터가 그리는 섬광 상태는 바꾸지 않는다). 허용되면 기록하고 true.
   * 화면 전체 효과를 내는 다른 계층(VFX 모듈 화면 펄스)이 각성·처형 섬광과 같은 1초 3회·간격 334ms 예산을 나눠 쓰게 한다(Codex P1, c013).
   */
  admit(nowMs: number): boolean {
    if (!this.wouldAdmit(nowMs)) { this.blocked++; return false; }
    this.times[this.n++ % this.times.length] = nowMs;
    this.last = nowMs;
    this.granted++;
    return true;
  }

  /** 현재 섬광 세기(0..peak). 앞 60%는 유지, 뒤 40%에서 꺼진다. 대기 중인 우선 섬광을 먼저 건다(매 프레임 호출) */
  value(nowMs: number): number {
    this.pump(nowMs);
    if (nowMs >= this.until) return 0;
    const p = (nowMs - this.start) / this.dur;
    return p < 0.6 ? this.peak : this.peak * (1 - (p - 0.6) / 0.4);
  }

  reset(): void {
    this.until = -1;
    this.pendAt = -1;
    this.reservedUntil = -1;
  }
}
